import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { interval, of } from 'rxjs';
import { catchError, switchMap, startWith } from 'rxjs/operators';
import { parseISO } from 'date-fns';
import Swal from 'sweetalert2';
import { Orders, ProgressObjective } from 'src/app/models/Orders.model';
import { Status } from 'src/app/models/status';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { IconsService } from 'src/app/services/icons.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';
import {
  SortDirection,
  nextSortState,
  sortItems,
} from 'src/app/utils/table-sort';

/**
 * AssegnaOrdineListComponent — lista degli ordini da assegnare a un
 * operatore di magazzino. Mostra KPI banner con metriche aggregate
 * (totale imponibile, da assegnare, parziali, completi, bloccati) +
 * tabella ordini con action per assegnare o bloccare/sbloccare.
 *
 * Auto-refresh ogni 60s via `interval()` + takeUntilDestroyed (era
 * un `setInterval` mai clearato in ngOnDestroy → memory leak fix).
 *
 * Sub-modale `<app-assegna-ordine-dettagli>` (Chunk I.2 in arrivo)
 * aperto via row action ✏️ Assegna.
 *
 * Phase 1.5 / v2.10.0 — TS cleaned up (Chunk I.1):
 *   • dropped kendo-grid + 7 kendo-grid-column + 5 kendo-progressbar
 *     + 2 kendo-datepicker + kendo-grid-command-column + 4 mat-spinner
 *     (40 kendo/ion refs → 0 in HTML),
 *
 *   • dropped `ViewEncapsulation.None` (hack legacy per styling
 *     globale, ora componente completamente scopato col pattern
 *     Diakonix),
 *
 *   • dropped kendo imports `GridDataResult`, `PageChangeEvent`,
 *     `RowClassArgs`, `LabelSettings` + tutta la kendo-grid
 *     virtualization logic,
 *
 *   • dropped 33 console.log/debug,
 *
 *   • dropped ~15 dead state fields (monthlyGoal/dailyGoal/days/
 *     totalValue/accumulatedBalance/progressToGoal — obiettivi
 *     CALCULATED ma MAI VISUALIZZATI nell'HTML attuale),
 *
 *   🚨 FIX BUG: legacy aveva `setInterval(60s)` ma `ngOnDestroy`
 *   faceva solo `subscriber.unsubscribe()`, NON `clearTimeout(timer)`
 *   → setInterval continuava in background dopo destroy → memory
 *   leak + chiamate API inutili. Ora `interval()` RxJS +
 *   `takeUntilDestroyed` auto-pulisce.
 *
 *   🚨 FIX BUG: 3 property class-level (`totalAmount`,
 *   `totaleImporto`, `totz`) facevano `.reduce()` su `gridData` in
 *   init → sempre 0 perché gridData popolato dopo subscribe.
 *   Droppati, calcolo ora nel subscribe.next via `applyMetrics()`.
 *
 *   • switch a `inject()` + `takeUntilDestroyed(destroyRef)`,
 *   • `applyMetrics()` helper estratto da `getNewOrders` per
 *     calcolare KPI in modo testabile.
 *
 * Behaviour preserved 1:1: `getNewOrders` data fetch + filter
 * parziali/completi/bloccati, `searchNewOrders` con
 * `error_message.msg_code === 8` empty handling, `block`/`unblock`
 * con `iconService.changeIconTo*` + Swal feedback, `gotoDetails →
 * modalService.onAssegnaDet()`, `isInWork()` auto-suspend logic.
 */
@Component({
  selector: 'app-assegna-ordine-list',
  templateUrl: './assegna-ordine-list.component.html',
  styleUrls: ['./assegna-ordine-list.component.scss'],
  standalone: false,
})


export class AssegnaOrdineListComponent implements OnInit {

  // ── Injected services ─────────────────────────────────────────────
  private readonly ordersServ = inject(OrdersService);
  private readonly iconService = inject(IconsService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly modalService = inject(OpenModalService);

  // ── Data ──────────────────────────────────────────────────────────
  public gridData: Orders.IOrderNewList[] = [];
  public ordiniParziali: Orders.IOrderNewList[] = [];
  public objectives: ProgressObjective = new ProgressObjective(null);

  // ── KPI metrics (calculated in applyMetrics() after each fetch) ──
  public netValue = 0;
  public etValueTotal = 0;
  public etValuePartial = 0;
  public etValueBlocked = 0;
  public netValueLavoration = 0;
  public ordiniCompleti = 0;
  public blockedOrdersCount = 0;
  public completeOrdersPercentage = 0;
  public partialOrdersPercentage = 0;
  public blockedOrdersPercentage = 0;
  public percentageLavoration = 0;

  // ── UI state ──────────────────────────────────────────────────────
  public loading = false;
  public loadingForObj = false;
  public filterTerm = '';
  public selectedAssign: string | null = null;
  public selectedOrderId = -1;
  public sameIndex = true;
  /** Auto-refresh ogni 60s (era setInterval legacy mai clearato). */
  private readonly refreshInterval = 60000;

  // ── Date range filter (defaults: 2021-01-01 → today) ─────────────
  private readonly startDate = new Date(2021, 0, 1);
  /**
   * Fine range = "oggi" corrente.
   *
   * 🚨 FIX v2.14.2 (audit post-dashboard): pre-fix era
   * `readonly endDate = new Date()` come class-field init — pattern
   * che congela la data al momento della prima istanziazione del
   * componente. Se l'utente lascia aperta la pagina a cavallo di
   * mezzanotte, il filtro restava al giorno precedente escludendo
   * silenziosamente gli ordini appena arrivati.
   *
   * Ora è mutable e `refreshEndDate()` la riallinea in `ngOnInit`
   * (e può essere richiamato prima dei fetch principali se il caso
   * si presenta).
   */
  private endDate: Date = new Date();
  public filters: { startingDate: Date | null; endDate: Date } = {
    startingDate: this.startDate,
    endDate: this.endDate,
  };

  /** Riallinea endDate all'ora corrente. Vedi doc sopra. */
  private refreshEndDate(): void {
    this.endDate = new Date();
    this.filters.endDate = this.endDate;
  }

  public sortColumn: string | null = null;
  public sortDirection: SortDirection = 'asc';

  // ──────────────────────────────────────────────────────────────────
  // Computed getters (KPI extras).
  // ──────────────────────────────────────────────────────────────────

  /** Ordini da assegnare = totali - bloccati. */
  get ordiniDaAssegnare(): number {
    return (this.gridData?.length ?? 0) - (this.blockedOrdersCount ?? 0);
  }

  /** Imponibile da assegnare = netValue - bloccati. */
  get etValueDaAssegnare(): number {
    return (this.netValue ?? 0) - (this.etValueBlocked ?? 0);
  }

  /** Percentuale da assegnare sul totale. */
  get daAssegnarePercentage(): number {
    return this.netValue > 0
      ? (this.etValueDaAssegnare / this.netValue) * 100
      : 0;
  }

  /** True se l'ordine è parziale (`or_partial === 'S'`). */
  isPartial(item: Orders.IOrderNewList): boolean {
    return item.or_partial === 'S';
  }

  /** True se l'ordine è bloccato (`or_status === 'B'`). */
  isBlocked(item: Orders.IOrderNewList): boolean {
    return item.or_status === 'B';
  }

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loading = true;
    // Sync endDate all'ora corrente (fix bug audit — vedi doc).
    this.refreshEndDate();

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (Object.keys(params).length === 0) {
          this.startPolling();
        }
        else {
          const searchParam: string = params['search'] ?? '';
          const dateParam: string | undefined = params['data'];
          this.filters.startingDate = dateParam
            ? parseISO(dateParam)
            : null;
          this.cdr.detectChanges();
          this.searchOrders(searchParam);
        }
      });
  }

  /**
   * FIX BUG: era `setInterval(...)` con `timer` field mai clearato in
   * ngOnDestroy → memory leak. Ora `interval()` RxJS con
   * `takeUntilDestroyed` auto-pulisce alla distruzione del componente.
   */
  private startPolling(): void {
    this.loadingForObj = true;

    // Objectives polling.
    interval(this.refreshInterval)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.ordersServ.getObjectiveProgress().pipe(
            catchError(() => of(null)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data: any) => {
        this.objectives = data?.success ? data.data : null;
        this.loadingForObj = false;
        this.cdr.detectChanges();
      });

    // Orders polling (separato per isolare gli error path).
    interval(this.refreshInterval)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.ordersServ.getNewOrders().pipe(
            catchError(() => of(null)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((openOrdersData: any) => {
        const list = openOrdersData?.data ?? [];
        this.gridData = list;
        this.applyMetrics(list);
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Manual refresh.
  // ──────────────────────────────────────────────────────────────────

  reloadFunction(): void {
    this.loading = true;
    this.ordersServ
      .getNewOrders()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((openOrdersData: any) => {
        const list = openOrdersData?.data ?? [];
        this.gridData = list;
        this.applyMetrics(list);
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // KPI metrics (extracted helper, testable).
  // ──────────────────────────────────────────────────────────────────

  private applyMetrics(list: Orders.IOrderNewList[]): void {
    this.netValue = 0;
    this.etValuePartial = 0;
    this.etValueTotal = 0;
    this.etValueBlocked = 0;

    for (const item of list) {
      const v = item.or_net_value ?? 0;
      this.netValue += v;
      if (this.isPartial(item)) this.etValuePartial += v;
      else this.etValueTotal += v;
      if (item.tab_order_status_cod === 101 || item.or_status === 'B') {
        this.etValueBlocked += v;
      }
    }

    this.netValueLavoration = list
      .filter((o) => o.tab_order_status_cod === 10)
      .reduce((acc, o) => acc + (o.or_net_value ?? 0), 0);

    this.blockedOrdersCount = list.filter(
      (o) => o.tab_order_status_cod === 101 || o.or_status === 'B',
    ).length;

    this.ordiniParziali = list.filter((o) => o.or_partial === 'S');
    this.ordiniCompleti = list.length - this.ordiniParziali.length;

    const total1 = this.etValueTotal + this.etValuePartial;
    this.completeOrdersPercentage = total1
      ? (this.etValueTotal / total1) * 100
      : 0;
    this.partialOrdersPercentage = total1
      ? (this.etValuePartial / total1) * 100
      : 0;
    this.blockedOrdersPercentage = this.netValue
      ? (this.etValueBlocked / this.netValue) * 100
      : 0;
    this.percentageLavoration = this.netValue
      ? (this.netValueLavoration / this.netValue) * 100
      : 0;
  }

  // ──────────────────────────────────────────────────────────────────
  // Search.
  // ──────────────────────────────────────────────────────────────────

  searchOrders(searchString?: string): void {
    if (searchString !== undefined) this.filterTerm = searchString;

    if (!this.filterTerm || this.filterTerm.trim() === '') {
      this.reloadFunction();
      return;
    }

    this.loading = true;
    this.ordersServ
      .searchNewOrders(this.filterTerm)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((newOrdersData: IWsResult | null) => {
        if (
          !newOrdersData ||
          newOrdersData.error_message?.msg_code === 8
        ) {
          this.gridData = [];
          this.applyMetrics([]);
        } 
        else {
          const list = newOrdersData.data ?? [];
          this.gridData = list;
          this.applyMetrics(list);
        }
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  clearSearch(): void {
    this.filterTerm = '';
    this.filters.startingDate = this.startDate;
    this.filters.endDate = this.endDate;
    this.reloadFunction();
  }

  onStartDateChange(value: Date | string | null): void {
    this.filters.startingDate = !value
      ? null
      : value instanceof Date
        ? value
        : new Date(value);
    this.cdr.detectChanges();
  }

  onEndDateChange(value: Date | string | null): void {
    if (!value) return;
    this.filters.endDate = value instanceof Date ? value : new Date(value);
    this.cdr.detectChanges();
  }

  // ──────────────────────────────────────────────────────────────────
  // Detail navigation.
  // ──────────────────────────────────────────────────────────────────

  gotoDetails(order_id: number): void {
    this.selectedOrderId = order_id;
    this.modalService.onAssegnaDet();
  }

  onChildClose(result: boolean): void {
    if (result) this.reloadFunction();
  }

  // ──────────────────────────────────────────────────────────────────
  // Block / unblock actions.
  // ──────────────────────────────────────────────────────────────────

  block(order: Orders.IOrderNewList): void {
    this.iconService.changeIconToBlocked(order);
    this.ordersServ
      .lockNewOrder(String(order.or_gamma_id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IWsResult) => {
        if (!result?.success) return;
        this.reloadFunction();
        Swal.fire({
          icon: 'success',
          title: 'Bloccato!',
          text: 'Ordine bloccato con successo.',
          timer: 1500,
          timerProgressBar: true,
        });
      });
  }

  unblock(order: Orders.IOrderNewList): void {
    this.iconService.changeIconToWorking(order);
    this.ordersServ
      .unlockNewOrder(String(order.or_gamma_id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IWsResult) => {
        if (!result?.success) return;
        this.reloadFunction();
        Swal.fire({
          icon: 'success',
          title: 'Sbloccato!',
          text: 'Ordine sbloccato con successo.',
          timer: 1500,
          timerProgressBar: true,
        });
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Auto-suspend logic (preserved from legacy).
  // ──────────────────────────────────────────────────────────────────

  async isInWork(): Promise<boolean> {
    if (!this.ordersServ.isUser.getValue()) {
      this.sameIndex = false;
      return true;
    }
    const indexToSuspend = this.gridData.findIndex(
      (order) =>
        (order.tab_order_status_cod >= Status.StatusesUser.step1 &&
          order.tab_order_status_cod <= Status.StatusesUser.step4) ||
        order.tab_order_status_cod === Status.StatusesUser.intiated,
    );
    this.sameIndex =
      indexToSuspend !== -1
        ? this.gridData[indexToSuspend].order_id === this.selectedOrderId
        : false;

    if (indexToSuspend !== -1 && !this.sameIndex) {
      this.ordersServ
        .setStatus(
          this.gridData[indexToSuspend].order_id,
          Status.StatusesUser.blocked,
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result: IWsResult) => {
          if (!result?.success) {
            Swal.fire({
              icon: 'error',
              title: 'Attenzione!',
              text: "L'ordine non può essere sospeso.",
              timer: 1500,
              timerProgressBar: true,
            });
          }
        });
    }
    return true;
  }

  get filteredOrders(): Orders.IOrderNewList[] {
    const customer = this.selectedAssign;
    const start = this.filters.startingDate;
    const end = this.filters.endDate;
    let items = this.gridData.filter((o) => {
      if (customer && o.or_customer !== customer) return false;
      if (start && o.or_order_date && new Date(o.or_order_date) < start) {
        return false;
      }
      if (end && o.or_order_date && new Date(o.or_order_date) > end) {
        return false;
      }
      return true;
    });
    if (this.sortColumn) {
      items = sortItems(items, this.sortColumn, this.sortDirection);
    }
    return items;
  }

  sortBy(column: string): void {
    const next = nextSortState(this.sortColumn, this.sortDirection, column);
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
    this.cdr.detectChanges();
  }
  
}
